<script>
import {computed, onMounted, ref} from "vue";
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import useValidation from "@/use/Validation.js";
import {useContratTemplateStore} from "@/stores/ContratTemplate.js";
import useSharedSelectData from "@/use/SharedSelectData.js";
import axios from "axios";
import StrHRVariables from "@/components/StrHRVariables.vue";
import WCodeGeneration from "@/components/WCodeGeneration.vue";
import StrQuillEditorCorePdf from "@/components/StrQuillEditorCorePdf.vue";

export default {
    components: {StrQuillEditorCorePdf, WCodeGeneration, StrHRVariables},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
        archiveAction: Boolean,
        unarchiveAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.deleteAction || props.viewAction || props.archiveAction || props.unarchiveAction);
        const {loading, overlay, dialog, inputColor, backgroundColor} = useSharedData();
        const {simplePeriodicity, contratVariables} = useSharedSelectData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();
        const {mxRules} = useValidation();

        const contratTemplateStore = useContratTemplateStore();

        const stepper = ref(1);
        const contratTemplate = ref({
            determinedTime: false,
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/contratTemplates/${id}`).then((response) => {
                contratTemplate.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/contratTemplates', contratTemplate.value).then((response) => {
                    contratTemplateStore.addContratTemplate(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/contratTemplates', contratTemplate.value).then((response) => {
                    if (props.archiveAction || props.unarchiveAction) {
                        contratTemplateStore.deleteContratTemplate(contratTemplate.value);
                    } else {
                        contratTemplateStore.updateContratTemplate(response.data);
                    }
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/contratTemplates/${contratTemplate.value.id}`).then(() => {
                contratTemplateStore.deleteContratTemplate(contratTemplate.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        function archive() {
            contratTemplate.value.archive = true;
            update();
        }

        function unarchive() {
            contratTemplate.value.archive = false;
            update();
        }

        function addVariable(variable) {
            $refs.StrQuillEditor.addVariable(variable);
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            mxRules,
            contratTemplate,
            stepper,
            readonly,
            loading,
            overlay,
            dialog,
            inputColor,
            backgroundColor,
            simplePeriodicity,
            contratVariables,
            getBack,
            findById,
            save,
            update,
            remove,
            archive,
            unarchive,
            addVariable
        }
    },
};
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        max-width="900"
        scrollable
    >
        <v-card flat>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
                :archive-action="archiveAction"
                :unarchive-action="unarchiveAction"
            ></w-dialog-toolbar>
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                :archive-action="archiveAction"
                :unarchive-action="unarchiveAction"
                @remove="remove"
                @archive="archive"
                @unarchive="unarchive"
                @cancel="overlay = false"
            />
            <v-card-text>
                <v-stepper v-model="stepper" non-linear>
                    <v-stepper-header>
                        <v-stepper-step editable :step="1">
                            Définition
                        </v-stepper-step>
                        <v-divider/>
                        <v-stepper-step editable :step="2">
                            Modèle
                        </v-stepper-step>
                    </v-stepper-header>

                    <v-stepper-items>
                        <v-stepper-content :step="1">
                            <v-container fluid>
                                <v-form ref="form" lazy-validation>
                                    <v-row dense>
                                        <v-col cols="4">
                                            <w-label :label="$t('common.code')" required/>
                                            <w-code-generation
                                                v-model="contratTemplate.code"
                                                entity="CONTRAT_TEMPLATE"
                                                :load="addAction"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col cols="8">
                                            <w-label :label="$t('common.label')" required/>
                                            <w-text-field
                                                v-model="contratTemplate.name"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <v-switch
                                                v-model="contratTemplate.determinedTime"
                                                color="teal"
                                                :label="$t('contrat.cdd')"
                                                inset
                                                :readonly="readonly"
                                                hide-details
                                            ></v-switch>
                                        </v-col>
                                        <v-col cols="12">
                                            <w-fieldset :title="$t('contrat.period')">
                                                <v-row dense>
                                                    <v-col cols="6">
                                                        <w-label :label="$t('contrat.duration')" required/>
                                                        <w-text-field
                                                            v-model="contratTemplate.duration"
                                                            :readonly="readonly"
                                                            :disabled="!contratTemplate.determinedTime"
                                                            type="number"
                                                            :rules="contratTemplate.determinedTime ? [mxRules.required] : []"
                                                        ></w-text-field>
                                                    </v-col>
                                                    <v-col cols="6">
                                                        <w-label :label="$t('contrat.unit')" required/>
                                                        <w-enum-select
                                                            v-model="contratTemplate.periodicity"
                                                            :items="simplePeriodicity"
                                                            :readonly="readonly"
                                                            :disabled="!contratTemplate.determinedTime"
                                                            :rules="contratTemplate.determinedTime ? [mxRules.required] : []"
                                                        />
                                                    </v-col>
                                                </v-row>
                                            </w-fieldset>
                                        </v-col>
                                        <v-col cols="6">
                                            <v-switch
                                                v-model="contratTemplate.trialPeriod"
                                                color="blue"
                                                :label="$t('contrat.trial_period')"
                                                inset
                                                :readonly="readonly"
                                                hide-details
                                            ></v-switch>
                                        </v-col>
                                        <v-col cols="12">
                                            <w-fieldset :title="$t('contrat.period')">
                                                <v-row dense>
                                                    <v-col cols="6">
                                                        <w-label :label="$t('contrat.duration')" required/>
                                                        <w-text-field
                                                            v-model="contratTemplate.trialPeriodDuration"
                                                            :readonly="readonly"
                                                            :disabled="!contratTemplate.trialPeriod"
                                                            type="number"
                                                            :rules="contratTemplate.trialPeriod ? [mxRules.required] : []"
                                                        />
                                                    </v-col>
                                                    <v-col cols="6">
                                                        <w-label :label="$t('contrat.unit')" required/>
                                                        <w-enum-select
                                                            v-model="contratTemplate.trialPeriodPeriodicity"
                                                            :items="simplePeriodicity"
                                                            :readonly="readonly"
                                                            :disabled="!contratTemplate.trialPeriod"
                                                            :rules="contratTemplate.trialPeriod ? [mxRules.required] : []"
                                                        />
                                                    </v-col>
                                                </v-row>
                                            </w-fieldset>
                                        </v-col>
                                        <v-col cols="6">
                                            <v-switch
                                                v-model="contratTemplate.renewedTrialPeriod"
                                                color="orange"
                                                :label="$t('contrat.renewed_trial_period')"
                                                inset
                                                :readonly="readonly"
                                                hide-details
                                            ></v-switch>
                                        </v-col>
                                    </v-row>
                                </v-form>
                            </v-container>
                        </v-stepper-content>

                        <v-stepper-content :step="2">
                            <str-quill-editor
                                ref="StrQuillEditor"
                                v-model="contratTemplate.templateText"
                                :disabled="readonly"
                            ></str-quill-editor>

                            <str-quill-editor-core-pdf v-model="contratTemplate.templateText" hide/>
                        </v-stepper-content>
                    </v-stepper-items>
                </v-stepper>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <StrHRVariables
                    v-if="stepper === 2"
                    module="CONTRAT"
                    @addVariable="addVariable"
                />
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    @press="overlay = true"
                />
                <w-btn-archive
                    :show="archiveAction"
                    @press="overlay = true"
                />
                <w-btn-unarchive
                    :show="unarchiveAction"
                    @press="overlay = true"
                />
                <w-btn-print :show="stepper === 2"/>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
